// Beacon console shapes (apps/api/src/modules/feedback/admin-routes.ts) and their labels.
import type { AutofixState } from '@upm/domain';
import type { PillTone } from '../../ui';

export type FeedbackStatus = 'received' | 'planned' | 'in_progress' | 'done' | 'wont_do' | 'duplicate';
export type FeedbackKind = 'bug' | 'feature' | 'question';

export const STATUS_LABEL: Record<FeedbackStatus, string> = {
  received: 'Received', planned: 'Planned', in_progress: 'In progress', done: 'Done', wont_do: "Won't do", duplicate: 'Duplicate',
};
export const STATUS_TONE: Record<FeedbackStatus, PillTone> = {
  received: 'neutral', planned: 'purple', in_progress: 'info', done: 'ok', wont_do: 'neutral', duplicate: 'neutral',
};
export const KIND_LABEL: Record<FeedbackKind, string> = { bug: 'Bug', feature: 'Feature / Idea', question: 'Question' };
export const URGENCY_LABEL: Record<string, string> = { blocking: 'Blocking them', slowing: 'Slowing them down', fyi: 'Just FYI' };

export const AREAS = [
  ['clients', 'Clients'], ['engagements', 'Engagements'], ['jobs_tasks', 'Jobs & tasks'], ['invoices_billing', 'Invoices & billing'],
  ['documents', 'Documents'], ['esignature', 'E-signature'], ['client_portal', 'Client portal'], ['communications', 'Communications'],
  ['tax_advisory', 'Tax advisory'], ['reports_insights', 'Reports & insights'], ['settings', 'Settings'], ['other', 'Something else'],
] as const;
export const AREA_LABEL = Object.fromEntries(AREAS) as Record<string, string>;

export type QueueRow = {
  id: string; reference: string; cell_id: string; firm_key: string; firm_name: string | null; kind: FeedbackKind; area: string | null; title: string;
  title_source: 'user' | 'generated' | 'edited'; urgency: string | null; need_by: string | null; status: FeedbackStatus;
  status_reason: string | null; estimated_completion: string | null; estimate_source: 'manual' | 'release' | null;
  submitted_at: string; updated_at: string; first_response_at: string | null; done_at: string | null;
  reopen_until: string | null; reopened_at: string | null; reopen_count: number; notify_whole_firm: boolean;
  submitted_by: string; submitted_by_name: string; duplicate_of_id: string | null; duplicate_of_reference: string | null;
  attachment_count: number; internal_note_count: number; open_questions: number; watcher_count: number;
  duplicate_count: number; link_count: number; waiting_on_us: boolean;
  /** Firm activity you haven't opened yet (the queue only). */
  new_count?: number;
};

export type Message = {
  id: string; author_kind: 'requester' | 'watcher' | 'platform' | 'agent' | 'system'; author_name: string;
  kind: 'comment' | 'question' | 'reply' | 'internal_note'; visibility: 'shared' | 'internal'; body: string;
  answered_at: string | null; created_at: string; shared_from_note: boolean;
};

export type Attachment = {
  id: string; message_id: string | null; visibility: 'shared' | 'internal'; filename: string; mime_type: string;
  size_bytes: number; is_screenshot: boolean; annotated: boolean; source_title: string | null; created_at: string;
  scan: 'pending' | 'clean' | 'infected' | 'failed';
};

export type Merge = {
  id: string; duplicate_id: string; target_id: string; duplicate_reference: string; target_reference: string; reason: string;
  merged_by_name: string; merged_at: string; undo_until: string; undone_at: string | null; can_undo: boolean;
};

export type RequestDetail = QueueRow & {
  body: string;
  /** The backlog item this request is linked to, and where that item ships (null: not on the backlog). */
  release: {
    release_id: string | null; release_name: string | null; release_status: string | null; release_kind: 'release' | 'hotfix' | null;
    target_date: string | null; item_id: string; item_title: string; item_reference: string; item_status: string;
  } | null;
  diagnostics: Record<string, unknown>;
  /** The newest event this page shows; opening the page marks events up to it as seen. */
  last_sequence: string | null;
  messages: Message[];
  attachments: Attachment[];
  watchers: Array<{ id: string; name: string }>;
  events: Array<{ id: string; actor_kind: string; actor_name: string | null; type: string; at: string; payload?: { silent?: boolean } | null }>;
  merges: Merge[];
  duplicates: Array<{ id: string; reference: string; title: string; submitted_by_name: string }>;
  /** Staff-only links to related requests from any firm; firms never see them. */
  links: LinkedRequest[];
  /** The auto-fix runner's progress (null: it never started on this request). Firms never see it. */
  autofix: Autofix | null;
};

/** feedback_autofix_runs (migration 019), written by the runner through the agent API. */
export type Autofix = {
  state: AutofixState; steps: Partial<Record<AutofixState, { at: string; detail: string | null }>>;
  pr_url: string | null; review_url: string | null; agent_name: string; started_at: string; updated_at: string;
  stopped_at: string | null; stopped_by_name: string | null;
};

export type LinkedRequest = {
  id: string; reference: string; title: string; status: FeedbackStatus; cell_id: string; firm_key: string;
  firm_name: string | null; linked_by_name: string; linked_at: string;
};

export type Stats = {
  median_first_response_hours: number | null; no_reply_2_days: number; reopened_30_days: number;
  median_days_to_done: number | null; need_by_at_risk: number; open_count: number;
};

export { fmtCalendarDate } from '../../lib/format';

export const firmLabel = (r: Pick<QueueRow, 'cell_id' | 'firm_key'> & { firm_name?: string | null }) => `${r.firm_name || r.firm_key} · ${r.cell_id}`;
